import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notification } from '@routific/hive';
import { DropdownButton, MoreIcon } from '@routific/hive/v5';
import { observer } from 'mobx-react';
import { useStore } from '../../../../store/mst';
import { VisitDtoModelType } from '../../../../store/mst/models';
import DeleteVisitConfirmModal from '../../DeleteVisitConfirmModal';
import { deletedVisitFailedNotification } from '../../../../notifications/visits';
import getOrderActionsMenuItems from './getOrderActionsMenuItems';

interface OrderActionsButtonProps {
  visit: VisitDtoModelType;
  allowEdit?: boolean;
  allowDelete?: boolean;
  allowCreateCustomers?: boolean;
}

const OrderActionsButton: React.FC<OrderActionsButtonProps> = ({
  visit,
  allowEdit = false,
  allowDelete = false,
  allowCreateCustomers = false,
}) => {
  const { ui, model, customerProfiles } = useStore();
  const { t } = useTranslation('components');
  const { t: tNotification } = useTranslation('notifications');

  const [displayDeleteVisitConfirmModal, setDisplayDeleteVisitConfirmModal] = useState(false);
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);

  const disabled = !visit.canBeModified || visit.route?.isBeingRescheduled || visit.isBeingModified;
  const isCompleted = !!visit.isCompleted;
  const isArrivedStop = !!visit.isArrivedStop;
  const isScheduled = !!visit.isScheduled;
  const hasPublishedOrders = !!visit.route?.isDispatched;
  const isGeocoding = !!visit.isGeocoding;
  const isRescheduling = !!visit.route?.isBeingRescheduled;
  const createCustomerDisabled = !allowCreateCustomers || isGeocoding || !visit.uuid || isCreatingCustomer;

  const handleDeleteVisit = async () => {
    try {
      setDisplayDeleteVisitConfirmModal(false);
      if (visit.uuid) {
        await model.deleteVisit(visit.uuid);
      }
    } catch {
      notification(deletedVisitFailedNotification(tNotification, visit.id, visit.displayName));
    }
  };

  const onClickDelete = async () => {
    const skipConfirmation = ui.disableConfirmDeleteVisitModal && !isScheduled && !hasPublishedOrders;
    if (skipConfirmation) {
      if (visit.uuid) {
        await handleDeleteVisit();
      }
    } else {
      setDisplayDeleteVisitConfirmModal(true);
    }
  };

  const handleClickEdit = () => {
    ui.setOrderForm({ visible: true, visit, saving: false });
  };

  const handleCreateCustomer = async () => {
    if (!visit.uuid || createCustomerDisabled) return;

    setIsCreatingCustomer(true);
    try {
      await customerProfiles.createCustomersFromOrders([visit.uuid]);
    } catch {
      // Error notification handled in store
    } finally {
      setIsCreatingCustomer(false);
    }
  };

  const getEditTooltip = (): string | undefined => {
    if (isCompleted) return t('visitInfoCard.edit.tooltips.disabledCompleted');
    if (isArrivedStop) return t('visitInfoCard.edit.tooltips.disabledArrived');
    if (isRescheduling) return t('visitInfoCard.edit.tooltips.rescheduling');
    if (isGeocoding) return t('multiSelectActionsBar.orders.tooltip.isGeocoding');
    return undefined;
  };

  const getDeleteTooltip = (): string | undefined => {
    if (visit.isReattemptStop) return t('visitInfoCard.delete.tooltips.disabledReattempt');
    if (isCompleted) return t('visitInfoCard.delete.tooltips.disabledCompleted');
    if (isArrivedStop) return t('visitInfoCard.delete.tooltips.disabledArrived');
    if (isRescheduling) return t('visitInfoCard.delete.tooltips.rescheduling');
    if (isGeocoding) return t('multiSelectActionsBar.orders.tooltip.isGeocoding');
    return undefined;
  };

  const getCreateCustomerTooltip = (): string | undefined => {
    if (!allowCreateCustomers) return t('visitInfoCard.createCustomer.tooltips.disabled');
    if (isGeocoding) return t('multiSelectActionsBar.orders.tooltip.isGeocoding');
    return undefined;
  };

  const actionsLabel = t('visitInfoCard.actionsMenu');

  const menuItems = getOrderActionsMenuItems({
    t,
    disabled: !!disabled,
    createCustomerDisabled,
    isReattemptStop: !!visit.isReattemptStop,
    permissions: {
      allowEdit,
      allowDelete,
    },
    handlers: {
      onEdit: handleClickEdit,
      onDelete: onClickDelete,
      onCreateCustomer: handleCreateCustomer,
    },
    tooltips: {
      edit: getEditTooltip(),
      delete: getDeleteTooltip(),
      createCustomer: getCreateCustomerTooltip(),
    },
  });

  return (
    <>
      <DeleteVisitConfirmModal
        visible={displayDeleteVisitConfirmModal && !!visit}
        ordersCount={1}
        allowOptOut={!isScheduled}
        hasPublishedOrders={hasPublishedOrders}
        orderName={visit.displayName || ''}
        onOk={handleDeleteVisit}
        onCancel={() => setDisplayDeleteVisitConfirmModal(false)}
      />
      <DropdownButton
        hierarchy="tertiary"
        buttonType="icon-only"
        size="md"
        icon={<MoreIcon size="md" />}
        align="end"
        aria-label={actionsLabel}
        menuAriaLabel={actionsLabel}
        data-cy="order-actions-trigger-btn"
        data-testid="order-actions-trigger-btn"
        items={menuItems}
      />
    </>
  );
};

export default observer(OrderActionsButton);
